import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';

import { FilterBarService } from '~shared/services/filter-bar.service';
import { CustomPlpState } from '~app/modules/catalog/store';
import * as fromCatalog from '../../store';
import { isTagsVisible } from '~app/modules/filters/store';
import { Category } from '~app/modules/generic/models';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import {
  FilterAreaInteractionApplicationEvent,
  OptimizeApplicationEvent,
} from '~shared/modules/application-event/models';
import { Product, SeoContent } from '~shared/models';
import { FilterFacetValue, RecommendedProductsType } from '~app/modules/catalog/models';
import { ApplicationEventFilterAreaInteractionType } from '~shared/modules/application-event/constants';
import { CategoryPageCategoryFilterListService } from '~app/modules/filters/services/category-page-category-filter-list.service';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { FeatureToggleEnum, NEWSLETTER_PAGE_SOURCE, NUMBER_2000, NUMBER_40 } from '~shared/constants';
import { TitleService } from '~shared/services/title.service';
import { SpecificFilterBarService, SpecificFilterTypeConfig } from '~shared/services/specific-filter-bar.service';
import { StoryblokApiResponse } from '~app/modules/storyblok/models/storyblok-api-response.model';
import { StoryblokComponent, StoryData } from 'storyblok-js-client';
import { Components } from '~app/modules/storyblok/constants';
import { PlpVariantRestyleService } from '~shared/services/plp-variant-restyle/plp-variant-restyle.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { ScrollUtils } from '~shared/utils/scroll.utils';

@Component({
  selector: 'app-catalog-products-list',
  templateUrl: './catalog-products-list.component.html',
  styleUrls: ['./catalog-products-list.component.scss'],
  animations: [
    trigger('inOutAnimation', [
      transition(':enter', [style({ height: 0 }), animate('200ms ease-out', style({ height: 225 }))]),
      transition(':leave', [style({ height: 225 }), animate('200ms', style({ height: 0 }))]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CatalogProductsListComponent implements OnInit {
  @Input() isMobile: boolean;
  @Input() isDesktop: boolean;
  @Input() isMobileOrTablet: boolean;
  @Input() searchQuery: string;
  @Input() searchCorrection: string;
  @Input() isSearchPage: boolean = false;
  @Input() isCategoryPage: boolean = false;
  @Input() isCustomPLPPage: boolean = false;
  @Input() products: Product[];
  @Input() recommendedProducts: Product[];
  @Input() recommendedProductsType: RecommendedProductsType;
  @Input() totalCount: number;
  @Input() queryParams: Params;
  @Input() selectedCategory: Category;
  @Input() isZeroResult: boolean;
  @Input() hasFilters: boolean;
  @Input() isExpired: boolean;
  @Input() isTablet: boolean;

  // This will depend on FF_MACO_STORYBLOK_PLPS
  @Input() seoContent: SeoContent;
  @Input() storyblokSeoContent: StoryblokApiResponse;

  private zeroResultsPage: boolean;
  private storyId: number;

  protected readonly components = Components;
  protected readonly customPLPNewsletterSource: NEWSLETTER_PAGE_SOURCE = NEWSLETTER_PAGE_SOURCE.CUSTOM_PLP;
  isCategoryListFilter$: Observable<boolean>;
  isTagsVisible$: Observable<boolean>;
  customPlpConfigContent$: Observable<CustomPlpState>;
  isFiltersBarOpened$: Observable<boolean>;
  showFilterFacets: boolean;
  showCategoryFacets: boolean;
  isStickyActions = false;
  layoutChanged = false;
  applicationEventFilterAreaInteractionType = ApplicationEventFilterAreaInteractionType;
  specificFilterType: SpecificFilterTypeConfig;
  mainEditable: string;
  hidePageTitle = false;
  storyblokTopContent: Array<StoryblokComponent<string>> = [];
  storyblokBottomContent: Array<StoryblokComponent<string>> = [];
  isPLP: boolean;
  isNewPLPVariantRestyle$: Observable<boolean>;

  get isFirstPage(): boolean {
    return !this.queryParams.offset || +this.queryParams.offset < NUMBER_40;
  }

  constructor(
    private store: Store<fromCatalog.CatalogState>,
    private filterBarService: FilterBarService,
    @Inject(DOCUMENT) private document: Document,
    private applicationEventService: ApplicationEventService,
    private cd: ChangeDetectorRef,
    private categoryPageCategoryFilterListService: CategoryPageCategoryFilterListService,
    private featureFlagService: ActiveFeaturesConfigcatService,
    private titleService: TitleService,
    private specificFilterBarService: SpecificFilterBarService,
    private plpVariantRestyleService: PlpVariantRestyleService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.initialTagVisible();
    this.fireOptimizeApplicationEvent();
    this.checkContentOnStoryblokEdit();
  }

  ngOnInit(): void {
    this.initialIsFilterBarOpen();
    this.initialIsSpecificFilterBarOpen();
    this.initializeCustomPLPConfig();
    this.initializeSelectedCategory();

    this.isCategoryListFilter$ =
      this.categoryPageCategoryFilterListService.isCategoryPageCategoryFilterListEnabledObservable$;
    this.isNewPLPVariantRestyle$ = this.plpVariantRestyleService.isNewPLPVariantRestyle();
    this.isPLP = this.plpVariantRestyleService.isPLP();
  }

  ngOnChanges() {
    this.zeroResultsPage = this.isZeroResult && !this.isExpired && !this.hasFilters;
    this.showFilterFacets = !this.zeroResultsPage;
    this.showCategoryFacets = (this.isSearchPage || this.isCategoryPage) && (this.isDesktop || this.isMobileOrTablet);
    this.addStoryblokContentIfExists();

    // change to after-loading position?
    this.scrollToProductListIfNeeded();
  }

  scrollToProductListIfNeeded(): void {
    const paginationOffset = this.route.snapshot.queryParamMap.get('offset');
    const pageZero = paginationOffset === '0';
    // Do not scroll on = page 0 for /content landing pages
    const pageZeroAndLanding = pageZero && this.router.url.includes('promoTag');
    if (pageZeroAndLanding) {
      return;
    }
    //If filter or sorting or pagination is applied
    if (this.router.url.includes('f.') || this.router.url.includes('orderBy') || paginationOffset) {
      setTimeout(() => ScrollUtils.scrollToFirstOfClass('catalog-products-list', 'smooth'), NUMBER_2000);
    }
  }

  toggleFilters(isOpened: boolean, type: ApplicationEventFilterAreaInteractionType): void {
    this.filterBarService.toggleFilterBar(isOpened);
    this.toggleScrollbar(isOpened);
    this.fireFilterInteractionEvent(type);
  }

  toggleSpecificFilters(type: ApplicationEventFilterAreaInteractionType): void {
    this.specificFilterBarService.toggleFilterBar(null);
    this.fireFilterInteractionEvent(type);
  }

  togglePriceFilter() {
    this.specificFilterBarService.toggleFilterBar({
      type: 'price',
    });
  }

  toggleCategoryFilter() {
    this.specificFilterBarService.toggleFilterBar({
      type: 'category',
    });
  }

  toggleAttributeFilter(filterFacetValue: FilterFacetValue) {
    this.specificFilterBarService.toggleFilterBar({
      type: 'attribute',
      options: {
        attribute: filterFacetValue,
      },
    });
  }

  onActionsStickyStatusChange(isSticky: boolean) {
    this.isStickyActions = isSticky;
  }

  onShowAllCategoryChildren(): void {
    if (!this.isMobileOrTablet) {
      return;
    }
    // update layout so sticky directive can recalculate again
    this.layoutChanged = true;
  }

  private initialTagVisible(): void {
    this.isTagsVisible$ = this.store.pipe(
      select(isTagsVisible),
      tap(() => this.cd.markForCheck())
    );
  }

  private initialIsFilterBarOpen(): void {
    this.isFiltersBarOpened$ = this.filterBarService.isFilterBarOpened.pipe(
      tap((isOpened: boolean) => {
        this.toggleScrollbar(isOpened);
        this.cd.markForCheck();
      })
    );
  }

  private initialIsSpecificFilterBarOpen(): void {
    this.specificFilterBarService.isSpecificFilterBarOpened.subscribe((filterType: SpecificFilterTypeConfig) => {
      this.toggleScrollbar(!!filterType);
      this.specificFilterType = filterType;
      this.cd.markForCheck();
    });
  }

  private initializeCustomPLPConfig(): void {
    if (this.isCustomPLPPage) {
      this.customPlpConfigContent$ = this.store.pipe(
        select(fromCatalog.getCustomPlpConfigContent),
        tap(() => this.cd.markForCheck())
      );
    }
  }

  private toggleScrollbar(isOpened: boolean): void {
    this.document.body.classList.toggle('noscroll', isOpened);
  }

  private fireOptimizeApplicationEvent(): void {
    this.applicationEventService.publish(
      new OptimizeApplicationEvent('optimize.activateNewCategoryNavigation', 'Category Navigation')
    );
  }

  private fireFilterInteractionEvent(type: ApplicationEventFilterAreaInteractionType): void {
    this.applicationEventService.publish(new FilterAreaInteractionApplicationEvent(type));
  }

  private initializeSelectedCategory(): void {
    if (this.selectedCategory?.name) {
      this.titleService.setTitle([{ title: 'TITLE.PAGE.CATEGORY', slug: this.selectedCategory.name }]);
      this.cd.markForCheck();
    }
  }

  private checkContentOnStoryblokEdit(): void {
    window?.storyblok?.on('input', (event: StoryblokEventPayload) => {
      window.storyblok.addComments(event.story.content, String(event.story.id));
      if (event.story.id === this.storyId) {
        this.applyStoryblokStoryData(event.story);
      }
    });
  }

  private applyStoryblokStoryData(story: StoryData): void {
    this.mainEditable = story.content._editable;
    this.hidePageTitle = story.content.hide_page_title;
    this.storyblokTopContent = story.content.topContent;
    this.storyblokBottomContent = story.content.bottomContent;
  }

  private resetStoryblokCStoryData(): void {
    this.mainEditable = '';
    this.hidePageTitle = false;
    this.storyblokTopContent = [];
    this.storyblokBottomContent = [];
  }

  private addStoryblokContentIfExists(): void {
    if (this.storyblokSeoContent) {
      const { story } = this.storyblokSeoContent;
      this.storyId = story.id;
      this.applyStoryblokStoryData(story);
    } else {
      this.resetStoryblokCStoryData();
    }
  }
}
